import { Component, OnInit } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { CandidatosApiService } from 'src/app/core/services/candidatos-api.service';

@Component({
  selector: 'app-candidato-perfil',
  standalone: false,
  templateUrl: './candidato-perfil.component.html',
  styleUrl: './candidato-perfil.component.scss',
  animations: [
    trigger('expandCollapse', [
      state('colapsado', style({ height: '0px', overflow: 'hidden', opacity: 0 })),
      state('expandido', style({ height: '*', opacity: 1 })),
      transition('colapsado <=> expandido', animate('300ms ease-in-out')),
    ])
  ]
})
export class CandidatoPerfilComponent implements OnInit {

  // 📋 OBJETO PRINCIPAL APPIAN - Estructura exacta
  candidatoData: any = {
    resumeId: null,
    name: '',
    lastName: '',
    phone: '',
    email: '',
    addressState: '',
    addressMunicipality: '',
    addressStreet: '',
    addressZip: '',
    addressColony: '',
    professionalGoal: '',
    desiredMonthlySalary: null,
    skills: '',
    achievments: '',
    hasLanguages: [],
    hasStudies: [],
    hasWorkExperience: [],
    hvpocCertificacion: [],
    hvpocEspecialidad: [],
    resumeUrl: '',
    institution: '',
    documentType: 'CV'
  };

  // 🔄 CONTROL DE ESTADOS DE EDICIÓN POR MÓDULO
  editingModules = {
    personalInfo: false,
    workExperience: false,
    studies: false,
    languages: false,
    certifications: false,
    specialties: false,
    skillsAchievements: false
  };

  // 📝 OBJETOS TEMPORALES PARA EDICIÓN
  editingData = {
    workExperience: { position: '', company: '', from: '', to: '', descriptionOfActivities: '' },
    study: { degree: '', institution: '', state: '', municipality: '', issueDate: '', isGraduate: false },
    language: { name: '', level: '', institution: '' },
    certification: { nombre: '', institucion: '', numero: '', fache_exp: '' },
    specialty: { area: '', annos_experiencia: null }
  };

  // 📇 ÍNDICES PARA EDICIÓN
  editIndexes: {
    workExperience: number | null;
    study: number | null;
    language: number | null;
    certification: number | null;
    specialty: number | null;
  } = {
    workExperience: null,
    study: null,
    language: null,
    certification: null,
    specialty: null
  };

  // 🔄 LOADING STATES
  loadingStates = {
    main: false,
    personalInfo: false,
    workExperience: false,
    studies: false,
    languages: false,
    certifications: false,
    specialties: false,
    skillsAchievements: false
  };

  constructor(
    private candidatosApiService: CandidatosApiService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarDatosCompletos();
  }

  // 🔄 ===== CARGA INICIAL DE DATOS =====
  cargarDatosCompletos() {
    this.loadingStates.main = true;
    const candidatoId = localStorage.getItem('candidatoId');

    if (!candidatoId) {
      console.error('No se encontró ID del candidato');
      this.loadingStates.main = false;
      return;
    }

    console.log('🔍 Cargando datos completos desde Appian...');
    
    this.candidatosApiService.obtenCandidato(candidatoId).subscribe(
      (response: any) => {
        console.log('✅ Datos recibidos de Appian:', response);
        
        if (response?.error) {
          console.warn('Candidato no encontrado en Appian');
          this.loadingStates.main = false;
          return;
        }

        // Actualizar candidatoData con la respuesta de Appian
        this.candidatoData = {
          ...this.candidatoData,
          ...response
        };

        console.log('📊 candidatoData actualizado:', this.candidatoData);
        this.loadingStates.main = false;
      },
      (error: any) => {
        console.error('❌ Error al cargar datos:', error);
        this.loadingStates.main = false;
      }
    );
  }

  // 👤 ===== INFORMACIÓN PERSONAL =====
  editarInformacionPersonal() {
    this.editingModules.personalInfo = true;
  }

  guardarInformacionPersonal() {
    this.loadingStates.personalInfo = true;
    
    const datosPersonales = {
      resumeId: this.candidatoData.resumeId,
      name: this.candidatoData.name,
      lastName: this.candidatoData.lastName,
      phone: this.candidatoData.phone,
      email: this.candidatoData.email,
      addressState: this.candidatoData.addressState,
      addressMunicipality: this.candidatoData.addressMunicipality,
      addressStreet: this.candidatoData.addressStreet,
      addressZip: this.candidatoData.addressZip,
      addressColony: this.candidatoData.addressColony,
      professionalGoal: this.candidatoData.professionalGoal,
      desiredMonthlySalary: this.candidatoData.desiredMonthlySalary,
      skills: this.candidatoData.skills,
      achievments: this.candidatoData.achievments
    };

    console.log('💾 Guardando información personal:', datosPersonales);

    this.candidatosApiService.guardarCandidatoAppian(datosPersonales).subscribe(
      (response: any) => {
        console.log('✅ Información personal guardada:', response);
        this.toastService.show('Información personal guardada exitosamente', 'success');
        this.editingModules.personalInfo = false;
        this.loadingStates.personalInfo = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar información personal:', error);
        this.toastService.show('Error al guardar información personal', 'danger');
        this.loadingStates.personalInfo = false;
      }
    );
  }

  cancelarEdicionPersonal() {
    this.editingModules.personalInfo = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 💼 ===== EXPERIENCIA LABORAL =====
  editarExperienciaLaboral() {
    this.editingModules.workExperience = true;
  }

  agregarExperiencia() {
    this.editingData.workExperience = { position: '', company: '', from: '', to: '', descriptionOfActivities: '' };
    this.editIndexes.workExperience = -1;
  }

  editarExperiencia(index: number) {
    this.editingData.workExperience = { ...this.candidatoData.hasWorkExperience[index] };
    this.editIndexes.workExperience = index;
  }

  guardarExperiencia() {
    if (!this.candidatoData.hasWorkExperience) {
      this.candidatoData.hasWorkExperience = [];
    }

    if (this.editIndexes.workExperience !== null && this.editIndexes.workExperience >= 0) {
      // Editar existente
      this.candidatoData.hasWorkExperience[this.editIndexes.workExperience] = { ...this.editingData.workExperience };
    } else {
      // Agregar nuevo
      this.candidatoData.hasWorkExperience.push({ ...this.editingData.workExperience });
    }

    this.editIndexes.workExperience = null;
    this.guardarModuloExperiencia();
  }

  eliminarExperiencia(index: number) {
    this.candidatoData.hasWorkExperience.splice(index, 1);
    this.guardarModuloExperiencia();
  }

  guardarModuloExperiencia() {
    this.loadingStates.workExperience = true;

    const datosExperiencia = {
      resumeId: this.candidatoData.resumeId,
      hasWorkExperience: this.candidatoData.hasWorkExperience
    };

    console.log('💾 Guardando experiencia laboral:', datosExperiencia);

    this.candidatosApiService.guardarCandidatoAppian(datosExperiencia).subscribe(
      (response: any) => {
        console.log('✅ Experiencia laboral guardada:', response);
        this.toastService.show('Experiencia laboral guardada exitosamente', 'success');
        this.editingModules.workExperience = false;
        this.loadingStates.workExperience = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar experiencia laboral:', error);
        this.toastService.show('Error al guardar experiencia laboral', 'danger');
        this.loadingStates.workExperience = false;
      }
    );
  }

  cancelarEdicionExperiencia() {
    this.editingModules.workExperience = false;
    this.editIndexes.workExperience = null;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 🎓 ===== ESTUDIOS =====
  editarEstudios() {
    this.editingModules.studies = true;
  }

  agregarEstudio() {
    this.editingData.study = { degree: '', institution: '', state: '', municipality: '', issueDate: '', isGraduate: false };
    this.editIndexes.study = -1;
  }

  editarEstudio(index: number) {
    this.editingData.study = { ...this.candidatoData.hasStudies[index] };
    this.editIndexes.study = index;
  }

  guardarEstudio() {
    if (!this.candidatoData.hasStudies) {
      this.candidatoData.hasStudies = [];
    }

    if (this.editIndexes.study !== null && this.editIndexes.study >= 0) {
      // Editar existente
      this.candidatoData.hasStudies[this.editIndexes.study] = { ...this.editingData.study };
    } else {
      // Agregar nuevo
      this.candidatoData.hasStudies.push({ ...this.editingData.study });
    }

    this.editIndexes.study = null;
    this.guardarModuloEstudios();
  }

  eliminarEstudio(index: number) {
    this.candidatoData.hasStudies.splice(index, 1);
    this.guardarModuloEstudios();
  }

  guardarModuloEstudios() {
    this.loadingStates.studies = true;

    const datosEstudios = {
      resumeId: this.candidatoData.resumeId,
      hasStudies: this.candidatoData.hasStudies
    };

    console.log('💾 Guardando estudios:', datosEstudios);

    this.candidatosApiService.guardarCandidatoAppian(datosEstudios).subscribe(
      (response: any) => {
        console.log('✅ Estudios guardados:', response);
        this.toastService.show('Estudios guardados exitosamente', 'success');
        this.editingModules.studies = false;
        this.loadingStates.studies = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar estudios:', error);
        this.toastService.show('Error al guardar estudios', 'danger');
        this.loadingStates.studies = false;
      }
    );
  }

  cancelarEdicionEstudios() {
    this.editingModules.studies = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 🌐 ===== IDIOMAS =====
  editarIdiomas() {
    this.editingModules.languages = true;
  }

  agregarIdioma() {
    this.editingData.language = { name: '', level: '', institution: '' };
    this.editIndexes.language = -1;
  }

  editarIdioma(index: number) {
    this.editingData.language = { ...this.candidatoData.hasLanguages[index] };
    this.editIndexes.language = index;
  }

  guardarIdioma() {
    if (!this.candidatoData.hasLanguages) {
      this.candidatoData.hasLanguages = [];
    }

    if (this.editIndexes.language !== null && this.editIndexes.language >= 0) {
      // Editar existente
      this.candidatoData.hasLanguages[this.editIndexes.language] = { ...this.editingData.language };
    } else {
      // Agregar nuevo
      this.candidatoData.hasLanguages.push({ ...this.editingData.language });
    }

    this.editIndexes.language = null;
    this.guardarModuloIdiomas();
  }

  eliminarIdioma(index: number) {
    this.candidatoData.hasLanguages.splice(index, 1);
    this.guardarModuloIdiomas();
  }

  guardarModuloIdiomas() {
    this.loadingStates.languages = true;

    const datosIdiomas = {
      resumeId: this.candidatoData.resumeId,
      hasLanguages: this.candidatoData.hasLanguages
    };

    console.log('💾 Guardando idiomas:', datosIdiomas);

    this.candidatosApiService.guardarCandidatoAppian(datosIdiomas).subscribe(
      (response: any) => {
        console.log('✅ Idiomas guardados:', response);
        this.toastService.show('Idiomas guardados exitosamente', 'success');
        this.editingModules.languages = false;
        this.loadingStates.languages = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar idiomas:', error);
        this.toastService.show('Error al guardar idiomas', 'danger');
        this.loadingStates.languages = false;
      }
    );
  }

  cancelarEdicionIdiomas() {
    this.editingModules.languages = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 🏆 ===== CERTIFICACIONES =====
  editarCertificaciones() {
    this.editingModules.certifications = true;
  }

  agregarCertificacion() {
    this.editingData.certification = { nombre: '', institucion: '', numero: '', fache_exp: '' };
    this.editIndexes.certification = -1;
  }

  editarCertificacion(index: number) {
    this.editingData.certification = { ...this.candidatoData.hvpocCertificacion[index] };
    this.editIndexes.certification = index;
  }

  guardarCertificacion() {
    if (!this.candidatoData.hvpocCertificacion) {
      this.candidatoData.hvpocCertificacion = [];
    }

    if (this.editIndexes.certification !== null && this.editIndexes.certification >= 0) {
      // Editar existente
      this.candidatoData.hvpocCertificacion[this.editIndexes.certification] = { ...this.editingData.certification };
    } else {
      // Agregar nuevo
      this.candidatoData.hvpocCertificacion.push({ ...this.editingData.certification });
    }

    this.editIndexes.certification = null;
    this.guardarModuloCertificaciones();
  }

  eliminarCertificacion(index: number) {
    this.candidatoData.hvpocCertificacion.splice(index, 1);
    this.guardarModuloCertificaciones();
  }

  guardarModuloCertificaciones() {
    this.loadingStates.certifications = true;

    const datosCertificaciones = {
      resumeId: this.candidatoData.resumeId,
      hvpocCertificacion: this.candidatoData.hvpocCertificacion
    };

    console.log('💾 Guardando certificaciones:', datosCertificaciones);

    this.candidatosApiService.guardarCandidatoAppian(datosCertificaciones).subscribe(
      (response: any) => {
        console.log('✅ Certificaciones guardadas:', response);
        this.toastService.show('Certificaciones guardadas exitosamente', 'success');
        this.editingModules.certifications = false;
        this.loadingStates.certifications = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar certificaciones:', error);
        this.toastService.show('Error al guardar certificaciones', 'danger');
        this.loadingStates.certifications = false;
      }
    );
  }

  cancelarEdicionCertificaciones() {
    this.editingModules.certifications = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 🎯 ===== ESPECIALIDADES =====
  editarEspecialidades() {
    this.editingModules.specialties = true;
  }

  agregarEspecialidad() {
    this.editingData.specialty = { area: '', annos_experiencia: null };
    this.editIndexes.specialty = -1;
  }

  editarEspecialidad(index: number) {
    this.editingData.specialty = { ...this.candidatoData.hvpocEspecialidad[index] };
    this.editIndexes.specialty = index;
  }

  guardarEspecialidad() {
    if (!this.candidatoData.hvpocEspecialidad) {
      this.candidatoData.hvpocEspecialidad = [];
    }

    if (this.editIndexes.specialty !== null && this.editIndexes.specialty >= 0) {
      // Editar existente
      this.candidatoData.hvpocEspecialidad[this.editIndexes.specialty] = { ...this.editingData.specialty };
    } else {
      // Agregar nuevo
      this.candidatoData.hvpocEspecialidad.push({ ...this.editingData.specialty });
    }

    this.editIndexes.specialty = null;
    this.guardarModuloEspecialidades();
  }

  eliminarEspecialidad(index: number) {
    this.candidatoData.hvpocEspecialidad.splice(index, 1);
    this.guardarModuloEspecialidades();
  }

  guardarModuloEspecialidades() {
    this.loadingStates.specialties = true;

    const datosEspecialidades = {
      resumeId: this.candidatoData.resumeId,
      hvpocEspecialidad: this.candidatoData.hvpocEspecialidad
    };

    console.log('💾 Guardando especialidades:', datosEspecialidades);

    this.candidatosApiService.guardarCandidatoAppian(datosEspecialidades).subscribe(
      (response: any) => {
        console.log('✅ Especialidades guardadas:', response);
        this.toastService.show('Especialidades guardadas exitosamente', 'success');
        this.editingModules.specialties = false;
        this.loadingStates.specialties = false;
        this.cargarDatosCompletos(); // Recargar datos completos
      },
      (error: any) => {
        console.error('❌ Error al guardar especialidades:', error);
        this.toastService.show('Error al guardar especialidades', 'danger');
        this.loadingStates.specialties = false;
      }
    );
  }

  cancelarEdicionEspecialidades() {
    this.editingModules.specialties = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // 🎯 ===== HABILIDADES Y LOGROS =====
  guardarHabilidadesLogros() {
    this.loadingStates.skillsAchievements = true;
    
    // Crear objeto con solo las habilidades y logros
    const datosHabilidadesLogros = {
      skills: this.candidatoData.skills,
      achievments: this.candidatoData.achievments
    };

    this.candidatosApiService?.guardarCandidatoAppian(datosHabilidadesLogros).subscribe(
      (response: any) => {
        console.log('✅ Habilidades y logros guardados exitosamente:', response);
        this.toastService.show('Habilidades y logros guardados exitosamente', 'success');
        this.loadingStates.skillsAchievements = false;
      },
      (error: any) => {
        console.error('❌ Error al guardar habilidades y logros:', error);
        this.toastService.show('Error al guardar habilidades y logros', 'danger');
        this.loadingStates.skillsAchievements = false;
      }
    );
  }

  // ️ ===== UTILIDADES =====
  
  // Métodos para cancelar edición individual
  cancelarEdicionItem() {
    this.editIndexes.workExperience = null;
    this.editIndexes.study = null;
    this.editIndexes.language = null;
    this.editIndexes.certification = null;
    this.editIndexes.specialty = null;
  }

  parseSkills(): string[] {
    return this.candidatoData.skills ? this.candidatoData.skills.split(';').filter((s: string) => s.trim()) : [];
  }

  parseAchievements(): string[] {
    return this.candidatoData.achievments ? this.candidatoData.achievments.split(';').filter((a: string) => a.trim()) : [];
  }

  // Métodos para habilidades y logros con datos del candidatoData
  getSkillsArray(): string[] {
    if (!this.candidatoData?.skills) return [];
    return this.candidatoData.skills.split(';').map((skill: string) => skill.trim()).filter((skill: string) => skill.length > 0);
  }

  getAchievementsArray(): string[] {
    if (!this.candidatoData?.achievments) return [];
    return this.candidatoData.achievments.split(';').map((achievement: string) => achievement.trim()).filter((achievement: string) => achievement.length > 0);
  }

  // Métodos para idiomas
  getLanguageProgress(level: string): number {
    switch (level?.toLowerCase()) {
      case 'básico': case 'basic': return 25;
      case 'intermedio': case 'intermediate': return 50;
      case 'avanzado': case 'advanced': return 75;
      case 'nativo': case 'native': case 'fluent': return 100;
      default: return 50;
    }
  }

  // Métodos de edición para habilidades y logros
  editarHabilidadesLogros() {
    this.editingModules.skillsAchievements = true;
  }

  cancelarEdicionHabilidadesLogros() {
    this.editingModules.skillsAchievements = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }

  // Método para cancelar edición de información personal
  cancelarEdicionInformacionPersonal() {
    this.editingModules.personalInfo = false;
    this.cargarDatosCompletos(); // Recargar para descartar cambios
  }
}